/**
 * Meta Pixel Service
 *
 * Centralized wrapper for Meta (Facebook) Pixel tracking.
 * Works alongside GA4 (analytics.ts) and Firestore (eventTracker.ts).
 *
 * Cookie consent integration:
 * - Pixel loads with fbq('consent', 'revoke') by default in index.html
 * - CookieConsentBanner calls metaPixel.grantConsent() on "Tümünü Kabul Et"
 * - Events are queued by fbq even before consent — fired once granted
 *
 * Standard Meta events used (for best Ads Manager compatibility):
 * - PageView          → automatic on every page load (index.html)
 * - ViewContent        → page/section views (premium, paketler, rapor, landing)
 * - Lead              → sign up / registration completed
 * - InitiateCheckout  → checkout started (premium or sanayi kredi)
 * - Purchase          → successful payment
 * - Search            → AI tool usage (web/price/video search)
 * - AddToWishlist     → ikna screen CTA click (interest signal)
 * - CompleteRegistration → user upgrade anonymous → registered
 * - Custom events      → sanayi funnel, feature usage, etc.
 *
 * @see https://developers.facebook.com/docs/meta-pixel/reference
 */

// ============================================================================
// TYPES
// ============================================================================

// fbq is loaded globally from index.html
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

type MetaStandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'Lead'
  | 'InitiateCheckout'
  | 'Purchase'
  | 'Search'
  | 'AddToWishlist'
  | 'CompleteRegistration'
  | 'Contact'
  | 'StartTrial';

interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

// ============================================================================
// CORE
// ============================================================================

function fbq(command: string, event: string, params?: EventParams): void {
  if (typeof window === 'undefined' || !window.fbq) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[MetaPixel] fbq not available:', command, event, params || '');
    }
    return;
  }

  try {
    if (params) {
      window.fbq(command, event, params);
    } else {
      window.fbq(command, event);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[MetaPixel]', command, event, params || '');
    }
  } catch (error) {
    // Silent fail — pixel should never break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[MetaPixel] Error:', error);
    }
  }
}

function trackStandard(event: MetaStandardEvent, params?: EventParams): void {
  fbq('track', event, params);
}

function trackCustom(event: string, params?: EventParams): void {
  fbq('trackCustom', event, params);
}

// ============================================================================
// EXPORTED API
// ============================================================================

export const metaPixel = {

  // --------------------------------------------------------------------------
  // CONSENT (no-op: Meta Pixel runs without consent gate in TR/KVKK region)
  // --------------------------------------------------------------------------

  /** No-op — kept for CookieConsentBanner compatibility */
  grantConsent: (): void => {},

  /** No-op — kept for CookieConsentBanner compatibility */
  revokeConsent: (): void => {},

  // --------------------------------------------------------------------------
  // ADVANCED MATCHING
  // --------------------------------------------------------------------------

  /**
   * Send hashed user data to Meta for better attribution.
   * Call after login/signup — Meta auto-hashes before sending.
   * @see https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
   */
  setUserData: (params: {
    email?: string | null;
    firstName?: string | null;
    userId?: string;
  }): void => {
    if (typeof window === 'undefined' || !window.fbq) return;
    if (!params.email && !params.firstName && !params.userId) return;

    try {
      const userData: Record<string, string> = {};

      if (params.email) {
        userData.em = params.email.toLowerCase().trim();
      }
      if (params.firstName) {
        userData.fn = params.firstName.toLowerCase().trim();
      }
      if (params.userId) {
        userData.external_id = params.userId;
      }

      // Re-init pixel with user data — Meta merges this with existing pixel
      window.fbq('init', '4191138747696703', userData);

      if (process.env.NODE_ENV === 'development') {
        console.log('[MetaPixel] Advanced Matching set:', Object.keys(userData));
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[MetaPixel] Advanced Matching error:', error);
      }
    }
  },

  /** Clear user data on logout */
  clearUserData: (): void => {
    if (typeof window === 'undefined' || !window.fbq) return;
    try {
      window.fbq('init', '4191138747696703', {});
    } catch {}
  },

  // --------------------------------------------------------------------------
  // PAGE VIEWS (ViewContent)
  // --------------------------------------------------------------------------

  /** Landing page viewed */
  trackLandingView: (): void => {
    trackStandard('ViewContent', {
      content_name: 'Landing Page',
      content_category: 'landing',
    });
  },

  /** Premium page viewed */
  trackPremiumPageView: (userType?: string): void => {
    trackStandard('ViewContent', {
      content_name: 'Premium Page',
      content_category: 'premium',
      content_type: 'subscription',
      value: 199,
      currency: 'TRY',
      ...(userType && { user_type: userType }),
    });
  },

  /** Sanayi Raporu paketler page viewed */
  trackPaketlerPageView: (): void => {
    trackStandard('ViewContent', {
      content_name: 'Sanayi Raporu Paketler',
      content_category: 'sanayi_paketler',
      content_type: 'product',
    });
  },

  /** Chat page viewed (araba) */
  trackChatPageView: (): void => {
    trackStandard('ViewContent', {
      content_name: 'Chat - Araba',
      content_category: 'chat',
    });
  },

  /** Sanayi Raporu wizard page viewed */
  trackSanayiRaporuPageView: (): void => {
    trackStandard('ViewContent', {
      content_name: 'Sanayi Raporu Wizard',
      content_category: 'sanayi_raporu',
    });
  },

  /** Rapor detail page viewed */
  trackRaporDetailView: (raporId: string): void => {
    trackStandard('ViewContent', {
      content_name: 'Rapor Detail',
      content_category: 'rapor',
      content_ids: raporId,
    });
  },

  // --------------------------------------------------------------------------
  // AUTH (Lead / CompleteRegistration)
  // --------------------------------------------------------------------------

  /** User signed up (new registration) */
  trackSignUp: (method: 'google' | 'apple' | 'email'): void => {
    trackStandard('Lead', {
      content_name: 'Sign Up',
      content_category: 'auth',
      method,
    });
  },

  /** User login */
  trackLogin: (method: 'google' | 'apple' | 'email'): void => {
    trackStandard('CompleteRegistration', {
      content_name: 'Login',
      method,
      status: true,
    });
  },

  /** Anonymous → Registered upgrade */
  trackAccountUpgrade: (): void => {
    trackStandard('CompleteRegistration', {
      content_name: 'Account Upgrade',
      content_category: 'upgrade',
      status: true,
    });
  },

  // --------------------------------------------------------------------------
  // PREMIUM FUNNEL
  // --------------------------------------------------------------------------

  /** Premium modal shown (limit reached, upsell) */
  trackPremiumModalView: (source: string): void => {
    trackCustom('PremiumModalView', {
      source,
    });
  },

  /** Premium modal CTA clicked */
  trackPremiumModalCTA: (source: string): void => {
    trackCustom('PremiumModalCTA', {
      source,
    });
  },

  /** Checkout started (Premium subscription) */
  trackPremiumCheckoutStart: (ctaLocation: string, price?: number): void => {
    trackStandard('InitiateCheckout', {
      content_name: 'Premium Abonelik',
      content_category: 'premium',
      content_type: 'subscription',
      value: price ?? 199,
      currency: 'TRY',
      num_items: 1,
      cta_location: ctaLocation,
    });
  },

  /** Successful premium purchase */
  trackPremiumPurchase: (transactionId: string, price?: number): void => {
    trackStandard('Purchase', {
      content_name: 'Premium Abonelik',
      content_type: 'subscription',
      value: price ?? 199,
      currency: 'TRY',
      transaction_id: transactionId,
    });
  },

  // --------------------------------------------------------------------------
  // SANAYI RAPORU FUNNEL
  // --------------------------------------------------------------------------

  /** Sanayi session created (wizard started) */
  trackSanayiSessionCreated: (): void => {
    trackCustom('SanayiSessionCreated');
  },

  /** First message sent in sanayi wizard */
  trackSanayiFirstMessage: (): void => {
    trackCustom('SanayiFirstMessage');
  },

  /** Pattern ready (≥70%) — closer prompt triggered */
  trackSanayiPatternReady: (patternScore: number): void => {
    trackCustom('SanayiPatternReady', { patternScore });
  },

  /** Final confirmation modal opened */
  trackSanayiFinalConfirmation: (): void => {
    trackCustom('SanayiFinalConfirmation');
  },

  /** İkna screen viewed */
  trackSanayiIknaView: (mode: string): void => {
    trackStandard('ViewContent', {
      content_name: 'Sanayi Ikna Screen',
      content_category: 'sanayi_ikna',
      content_type: mode,
    });
  },

  /** İkna screen CTA clicked (buy or view) */
  trackSanayiIknaCTA: (ctaType: 'buy' | 'view'): void => {
    trackStandard('AddToWishlist', {
      content_name: 'Sanayi Raporu',
      content_category: 'sanayi',
      content_type: ctaType,
    });
  },

  /** Rapor fully viewed (past ikna barrier) */
  trackSanayiReportView: (method: string): void => {
    trackCustom('SanayiReportUnlocked', { method });
  },

  /** Sanayi kredi checkout started */
  trackSanayiCheckoutStart: (paketId?: string): void => {
    trackStandard('InitiateCheckout', {
      content_name: 'Sanayi Raporu Kredi',
      content_category: 'sanayi',
      content_type: 'credit',
      ...(paketId && { content_ids: paketId }),
    });
  },

  /** Sanayi kredi payment successful */
  trackSanayiCheckoutSuccess: (value?: number, currency?: string, paketId?: string): void => {
    trackStandard('Purchase', {
      value: value ?? 0,
      currency: currency ?? 'TRY',
      content_name: 'Sanayi Raporu Kredi',
      content_category: 'sanayi',
      content_type: 'credit',
      ...(paketId && { content_ids: paketId }),
    });
  },

  /** Sanayi kredi payment failed */
  trackSanayiCheckoutFail: (): void => {
    trackCustom('SanayiCheckoutFail');
  },

  // --------------------------------------------------------------------------
  // FEATURE USAGE
  // --------------------------------------------------------------------------

  /** Message sent in chat */
  trackMessageSent: (): void => {
    trackCustom('MessageSent');
  },

  /** New conversation created */
  trackConversationCreated: (category: string): void => {
    trackCustom('ConversationCreated', { category });
  },

  /** AI tool used (web/price/video search) */
  trackToolUse: (toolType: 'video' | 'price' | 'web'): void => {
    trackStandard('Search', {
      content_category: 'ai_tool',
      search_string: toolType,
    });
  },

  /** Media uploaded */
  trackMediaUpload: (mediaType: 'image' | 'video' | 'audio'): void => {
    trackCustom('MediaUpload', { media_type: mediaType });
  },
};
